import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { isEmptyObject } from '../../core/util/object';
import LoadingScreen from '../SolvPatternLibrary/LoadingScreen';
import TosUpdate from '../Account/TosUpdate';
import {
  receiveAccountSummary,
  accountSummaryError,
  accountSummaryLoading,
} from '../../actions/account';
import { resetLoginData, accountFetching } from '../../actions/login';
import { apiGetDispatchable, apiPostDispatchable } from '../../core/dapi';
import { getAccountSummaryById } from '../../core/dapi/account';
import history from '../../core/history';
import { getAccountId } from '../../core/auth';
import { clearUserInfo } from '../../core/session';
import { setSolvTosAccepted } from '../../core/dapi/account';
import { noOp } from '../../actions';
import { FETCH_LOGIN_ACCOUNT } from '../../sagas/account';
import { LOGIN_PERSISTED } from '../../core/analytics/events';
import { analyticsTrackEvent } from '../../core/analytics';
import logger from '../../core/logger';
import withAuthentication from '../../core/login/withAuthentication';
import { withCookies, Cookies } from 'react-cookie';

const mapStateToProps = state => ({
  accountSummary: state.account.summary,
  accountSummaryError: state.account.accountSummaryError,
  isAccountSummaryLoading: state.account.accountSummaryLoading || false,
  loginAccount: state.login.account,
  persistentStateLoaded: state.runtime.persistentStateLoaded,
  loginAccountFetching: state.login.fetching || false,
  requiresTosConsent: !!state.runtime.requiresTosConsent,
  loginAccountError: !!state.login.accountError,
});

const mapDispatchToProps = dispatch => ({
  fetchAccountSummary: (accountId, isAccountSummaryLoading) => {
    if (isAccountSummaryLoading) return;
    const onError = errorMessage => {
      logger.error('Error fetching account summary: ', errorMessage);
      return accountSummaryError(errorMessage);
    };

    dispatch(accountSummaryLoading());
    const url = getAccountSummaryById(accountId);
    dispatch(apiGetDispatchable(url, receiveAccountSummary, onError));
  },

  fetchLoginAccount: accountId => {
    dispatch(accountFetching());
    dispatch({
      type: FETCH_LOGIN_ACCOUNT,
      accountId,
    });
  },

  setTosAccepted: accountId => {
    const onSuccess = () => {
      const url = getAccountSummaryById(accountId);
      dispatch(apiGetDispatchable(url, receiveAccountSummary, accountSummaryError));
      return noOp();
    };

    dispatch(apiPostDispatchable(setSolvTosAccepted(accountId), {}, onSuccess, noOp));
  },

  clearLogin: cookies => {
    dispatch(resetLoginData());
    clearUserInfo(cookies);
  },
});

export class Wrapper extends Component {
  static propTypes = {
    accountSummary: PropTypes.object,
    accountSummaryError: PropTypes.string,
    children: PropTypes.any.isRequired,
    clearLogin: PropTypes.func.isRequired,
    fetchAccountSummary: PropTypes.func,
    isAccountSummaryLoading: PropTypes.bool,
    loginAccount: PropTypes.object,
    loginRequired: PropTypes.bool,
    persistentStateLoaded: PropTypes.bool,
    nonBlocking: PropTypes.bool,
    returnAfterLogin: PropTypes.bool,
    setTosAccepted: PropTypes.func,
    loginAccountFetching: PropTypes.bool,
    fetchLoginAccount: PropTypes.func,
    isAuthorized: PropTypes.bool.isRequired,
    isLoggedIn: PropTypes.bool.isRequired,
    cookies: PropTypes.instanceOf(Cookies),
    requiresTosConsent: PropTypes.bool,
    loginAccountError: PropTypes.bool,
  };

  static defaultProps = {
    loginRequired: true,
    nonBlocking: false,
    returnAfterLogin: true,
  };

  constructor(props) {
    super(props);
    this.isClearingLogin = false;
    this.hasTrackedPersistedLogin = false;
  }

  componentDidMount = () => {
    const { isAuthorized } = this.props;
    if (isAuthorized && history.length === 0) {
      const accountId = getAccountId();
      this.trackPersistedLogin(accountId);
    }
    this.handleAccountData();
  };

  componentDidUpdate = prevProps => {
    if (this.didJustLogIn(prevProps)) {
      this.isClearingLogin = false;
    }
    if (this.didJustLogOut(prevProps)) {
      this.handleAccountError();
    }
    if (this.accountDataFetchingFailed(prevProps)) {
      this.isClearingLogin = false;
      this.handleAccountError();
    }
    this.handleAccountData();
  };

  accountDataFetchingFailed = prevProps => {
    const { accountSummaryError, loginAccountError } = this.props;
    return (
      (!prevProps.accountSummaryError && accountSummaryError) ||
      (!prevProps.loginAccountError && loginAccountError)
    );
  };

  // For pages that required login (loginRequired), we never know if they'll need loginAccount or accountSummary (at least historically, some neeed one, some the other, some both).
  // So, going forward, we're going to mark this component as in loading state until both these slices of data are loaded
  hasAllAccountData = (loginAccount, accountSummary) =>
    !isEmptyObject(loginAccount) && !isEmptyObject(accountSummary);

  // We use this function to determine if there's any account data loaded in state (either in memory or coming from session storage), while the user doesn't have a valid authorization (invalid token or no token).
  hasSomeAccountData = (loginAccount, accountSummary) =>
    !isEmptyObject(loginAccount) || !isEmptyObject(accountSummary);

  didJustLogIn = prevProps => !prevProps.isLoggedIn && this.props.isLoggedIn;

  didJustLogOut = prevProps => prevProps.isLoggedIn && !this.props.isLoggedIn;

  handleAccountError = () => {
    if (this.isClearingLogin) {
      return;
    }
    this.isClearingLogin = true;
    const { pathname, search } = window.location;
    const { clearLogin, loginRequired, returnAfterLogin, cookies } = this.props;

    clearLogin(cookies);
    if (loginRequired) {
      if (returnAfterLogin) {
        if (!/^\/login\/next/.test(pathname)) {
          history.replace(`/account/login/next${pathname}${search}`);
        }
      } else {
        history.replace('/account/login');
      }
    }
  };

  handleAccountData = () => {
    const {
      loginAccount,
      fetchAccountSummary,
      isAccountSummaryLoading,
      loginAccountFetching,
      fetchLoginAccount,
      accountSummary,
      isAuthorized,
      loginRequired,
      requiresTosConsent,
    } = this.props;

    if (isAuthorized && !requiresTosConsent) {
      const accountId = getAccountId();
      if (!accountId) {
        return this.handleAccountError();
      }
      if (isEmptyObject(accountSummary)) {
        fetchAccountSummary(accountId, isAccountSummaryLoading, this.handleAccountError);
      }
      if (isEmptyObject(loginAccount) && !loginAccountFetching) {
        fetchLoginAccount(accountId);
      }
    }
    if (!isAuthorized && (loginRequired || this.hasSomeAccountData(loginAccount, accountSummary))) {
      return this.handleAccountError();
    }
    return null;
  };

  isLoading = () => {
    const {
      nonBlocking,
      persistentStateLoaded,
      loginAccount,
      loginRequired,
      accountSummary,
    } = this.props;

    if (nonBlocking) {
      return false;
    }
    if (!persistentStateLoaded) {
      return true;
    }

    return loginRequired && !this.hasAllAccountData(loginAccount, accountSummary);
  };

  trackPersistedLogin = accountId => {
    if (!this.hasTrackedPersistedLogin) {
      analyticsTrackEvent(LOGIN_PERSISTED, { accountId });
      this.hasTrackedPersistedLogin = true;
    }
  };

  render = () => {
    if (this.isLoading()) {
      return <LoadingScreen />;
    }
    const { accountSummary, setTosAccepted, clearLogin } = this.props;

    const content = React.cloneElement(this.props.children, { accountSummary });

    if (accountSummary && accountSummary.requires_tos_consent_update) {
      return (
        <div>
          {content}
          <TosUpdate
            accountId={accountSummary.id}
            setTosAccepted={setTosAccepted}
            clearLogin={clearLogin}
          />
        </div>
      );
    }

    return content;
  };
}

export default withAuthentication(
  withCookies(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(Wrapper)
  )
);
